import {useMemo, useState} from 'react';
import {trpc, createMutationErrorHandler} from '@common-web/lib/trpc';
import {Card, CardContent, CardHeader, CardTitle} from '@common-web/components/ui/card';
import {Badge} from '@common-web/components/ui/badge';
import {Skeleton} from '@common-web/components/ui/skeleton';
import {Input} from '@common-web/components/ui/input';
import {Label} from '@common-web/components/ui/label';
import {Button} from '@common-web/components/ui/button';
import {PageHeader} from '@common-web/components/page-header';
import {Alert, AlertDescription, AlertTitle} from '@common-web/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@common-web/components/ui/dialog';
import {
  AlertCircle,
  Building2,
  ChevronDown,
  ChevronRight,
  Globe,
  Loader2,
  Search,
  Settings,
} from 'lucide-react';
import {toast} from '@common-web/hooks/use-toast';
import {CODE_PERMISSIONS} from '@common/permissions';
import {useHasPermission} from '@common-web/hooks/use-permissions';
import {CLIENT_SLUGS} from '@common/constants/clients';
import {
  MENU_FLAGS,
  type ClientFeaturesShape,
  type MenuKey,
  isMenuFlagVisibleForClient,
  resolveMenuItemEnabled,
  resolveResourceFoldersEnabled,
} from '@common/menuFlags';
import type {AccountsViewConfig} from '@common/accountsView';
import {ClientFeatureFlagControls} from './components/clientFeatureConfig';
import {AccountsViewConfigSection} from './components/accountsViewConfig';
import {ClientUsersSection} from './components/clientUsersSection';

type OverrideState = 'default' | 'on' | 'off';

// Temporary gate: only these tenants are surfaced/controllable on this screen
// for now. Matched as case-insensitive substrings against the client name
// (e.g. "Champion Health", "Quantum Health Benefits LLC", "Synrgy ...").
const VISIBLE_CLIENT_KEYWORDS = [
  CLIENT_SLUGS.QUANTUM,
  CLIENT_SLUGS.SYNRGY,
  CLIENT_SLUGS.CHAMP,
  CLIENT_SLUGS.HEALTHCUES,
];

function isVisibleClient(name: string): boolean {
  const lowered = name.toLowerCase();
  return VISIBLE_CLIENT_KEYWORDS.some((keyword) => lowered.includes(keyword));
}

export function ClientsList() {
  const canEdit = useHasPermission(CODE_PERMISSIONS.clients.manageFeatures.key);

  const {data: allClients, isLoading, error, refetch} = trpc.internal.client.list.useQuery();

  const clients = useMemo(() => allClients?.filter((c) => isVisibleClient(c.name)), [allClients]);

  // Tracks the clientId whose mutation is in-flight so we can disable just that
  // row's switches and show a small spinner without blocking the whole page.
  const [pendingClientId, setPendingClientId] = useState<string | null>(null);

  const [search, setSearch] = useState('');

  // Cards collapse by default so the list stays scannable; track which ones the
  // user has expanded.
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const toggleExpanded = (clientId: string) =>
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(clientId)) next.delete(clientId);
      else next.add(clientId);
      return next;
    });

  const filteredClients = useMemo(() => {
    if (!clients) return [];
    const q = search.trim().toLowerCase();
    if (!q) return clients;
    return clients.filter(
      (c) => c.name.toLowerCase().includes(q) || (c.domain ?? '').toLowerCase().includes(q),
    );
  }, [clients, search]);

  const updateFeatures = trpc.internal.client.updateFeatures.useMutation({
    onSuccess: (updated) => {
      const {sessionsInvalidated} = updated;
      toast({
        title: `Updated ${updated.name}`,
        description:
          sessionsInvalidated > 0
            ? `Invalidated ${sessionsInvalidated} active session${
                sessionsInvalidated === 1 ? '' : 's'
              }. Affected users will be signed out within the session cookie cache window (~5 min).`
            : 'Feature flags updated. No active sessions needed to be invalidated.',
      });
      refetch();
    },
    onError: createMutationErrorHandler('Error updating client features'),
    onSettled: () => setPendingClientId(null),
  });

  const handleMasterChange = (
    clientId: string,
    _current: ClientFeaturesShape,
    checked: boolean,
  ) => {
    setPendingClientId(clientId);
    // Only the master toggle changes — omit `menuOverrides` so the server
    // leaves them untouched.
    updateFeatures.mutate({
      clientId,
      features: {enhancedBrokerPortal: checked},
    });
  };

  const handleResourceFoldersChange = (clientId: string, checked: boolean) => {
    setPendingClientId(clientId);
    updateFeatures.mutate({
      clientId,
      features: {resourceFolders: checked},
    });
  };

  const handleRfcClientPortalAccessChange = (clientId: string, checked: boolean) => {
    setPendingClientId(clientId);
    updateFeatures.mutate({
      clientId,
      features: {rfcClientPortalAccess: checked},
    });
  };

  const handleRfcAchAuthorizationChange = (clientId: string, checked: boolean) => {
    setPendingClientId(clientId);
    updateFeatures.mutate({
      clientId,
      features: {rfcAchAuthorization: checked},
    });
  };

  const handleSimplifiedResourceLayoutChange = (clientId: string, checked: boolean) => {
    setPendingClientId(clientId);
    updateFeatures.mutate({
      clientId,
      features: {simplifiedResourceLayout: checked},
    });
  };

  const handleLinkVbaGroupChange = (clientId: string, checked: boolean) => {
    setPendingClientId(clientId);
    updateFeatures.mutate({
      clientId,
      features: {linkVbaGroup: checked},
    });
  };

  const handleVipChange = (clientId: string, checked: boolean) => {
    setPendingClientId(clientId);
    updateFeatures.mutate({
      clientId,
      features: {vip: checked},
    });
  };

  const handleExternalTpasChange = (clientId: string, checked: boolean) => {
    setPendingClientId(clientId);
    updateFeatures.mutate({
      clientId,
      features: {externalTpas: checked},
    });
  };

  const handleAppUserDeactivationChange = (clientId: string, checked: boolean) => {
    setPendingClientId(clientId);
    updateFeatures.mutate({
      clientId,
      features: {appUserDeactivation: checked},
    });
  };

  const handleNewBrokerUiChange = (clientId: string, checked: boolean) => {
    setPendingClientId(clientId);
    updateFeatures.mutate({
      clientId,
      features: {newBrokerUi: checked},
    });
  };

  const handleAccountsViewSave = (clientId: string, config: AccountsViewConfig) => {
    setPendingClientId(clientId);
    updateFeatures.mutate({clientId, features: {accountsView: config}});
  };

  const handleAccountsViewReset = (clientId: string) => {
    setPendingClientId(clientId);
    updateFeatures.mutate({clientId, features: {accountsView: null}});
  };

  const handleBrokerDocumentSelfServiceChange = (clientId: string, checked: boolean) => {
    setPendingClientId(clientId);
    updateFeatures.mutate({
      clientId,
      features: {brokerDocumentSelfService: checked},
    });
  };

  const handleBulkBrokerImportChange = (clientId: string, checked: boolean) => {
    setPendingClientId(clientId);
    updateFeatures.mutate({
      clientId,
      features: {bulkBrokerImportEnabled: checked},
    });
  };

  const handleViewAsBrokerChange = (clientId: string, checked: boolean) => {
    setPendingClientId(clientId);
    updateFeatures.mutate({
      clientId,
      features: {viewAsBroker: checked},
    });
  };

  const handleOpenEnrollmentDashboardChange = (clientId: string, checked: boolean) => {
    setPendingClientId(clientId);
    updateFeatures.mutate({
      clientId,
      features: {openEnrollmentDashboard: checked},
    });
  };

  const handleBrokerCommissionAmountChange = (clientId: string, checked: boolean) => {
    setPendingClientId(clientId);
    updateFeatures.mutate({
      clientId,
      features: {brokerCommissionAmount: checked},
    });
  };

  const handleOverrideChange = (
    clientId: string,
    current: ClientFeaturesShape,
    key: MenuKey,
    next: OverrideState,
  ) => {
    setPendingClientId(clientId);

    const merged: Partial<Record<MenuKey, boolean>> = {
      ...(current.menuOverrides ?? {}),
    };

    if (next === 'default') {
      delete merged[key];
    } else {
      merged[key] = next === 'on';
    }

    updateFeatures.mutate({
      clientId,
      features: {menuOverrides: merged},
    });
  };

  // Ticketing has paired config (PortalTicketConfig.idPrefix + brandedEmailSender);
  // when turning it on for a tenant that doesn't already have a config row, the
  // dialog below collects those values and submits them with the toggle so the
  // server can write both atomically.
  const [ticketingDialog, setTicketingDialog] = useState<{
    clientId: string;
    clientName: string;
    idPrefix: string;
    brandedEmailSender: string;
    reminderHours: number;
  } | null>(null);

  const handleTicketingChange = (
    clientId: string,
    clientName: string,
    existingConfig:
      | {idPrefix: string; brandedEmailSender: string; reminderHours: number}
      | null
      | undefined,
    checked: boolean,
  ) => {
    if (!checked) {
      // Disabling is unambiguous — just flip the flag; the server keeps the
      // config row around in case it's re-enabled later.
      setPendingClientId(clientId);
      updateFeatures.mutate({clientId, features: {ticketing: false}});
      return;
    }
    if (existingConfig) {
      // Re-enabling an already-configured tenant. No prompt needed.
      setPendingClientId(clientId);
      updateFeatures.mutate({clientId, features: {ticketing: true}});
      return;
    }
    // First-time enable — prompt for the required PortalTicketConfig fields.
    setTicketingDialog({
      clientId,
      clientName,
      idPrefix: '',
      brandedEmailSender: '',
      reminderHours: 12,
    });
  };

  const submitTicketingDialog = () => {
    if (!ticketingDialog) return;
    setPendingClientId(ticketingDialog.clientId);
    updateFeatures.mutate(
      {
        clientId: ticketingDialog.clientId,
        features: {ticketing: true},
        ticketingConfig: {
          idPrefix: ticketingDialog.idPrefix.trim(),
          brandedEmailSender: ticketingDialog.brandedEmailSender.trim(),
          reminderHours: ticketingDialog.reminderHours,
        },
      },
      {
        onSuccess: () => setTicketingDialog(null),
      },
    );
  };

  return (
    <div className='min-h-screen bg-gradient-to-br from-gray-50 via-white to-brand-blue/5'>
      <div className='flex flex-col gap-0'>
        <PageHeader
          title='Clients'
          description='Tenant-level configuration and feature flags. Changes apply to every user belonging to the client.'
          icon={<Building2 className='h-6 w-6' />}
          isLoading={isLoading}
        />

        <div className='px-8 py-6 max-w-7xl mx-auto w-full space-y-6'>
          {!canEdit && (
            <Alert className='bg-amber/5 border-amber/30'>
              <Settings className='h-4 w-4 text-amber' />
              <AlertTitle className='text-brand-navy'>View-only</AlertTitle>
              <AlertDescription className='text-brand-gray'>
                You can review tenant feature flags here, but only internal admins can change them.
              </AlertDescription>
            </Alert>
          )}

          {error && (
            <Alert variant='destructive'>
              <AlertCircle className='h-4 w-4' />
              <AlertTitle>Could not load clients</AlertTitle>
              <AlertDescription>{error.message}</AlertDescription>
            </Alert>
          )}

          {!isLoading && clients && clients.length > 0 && (
            <div className='relative'>
              <Search className='absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-gray' />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder='Search clients by name or domain…'
                className='pl-10 bg-white'
              />
            </div>
          )}

          {isLoading ? (
            <div className='space-y-4'>
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} className='h-32 w-full' />
              ))}
            </div>
          ) : (
            <div className='space-y-4'>
              {filteredClients.map((client) => {
                const features = (client.features ?? {}) as ClientFeaturesShape;
                const isPending = pendingClientId === client.id;
                const isExpanded = expandedIds.has(client.id);
                const overrideCount = Object.values(features.menuOverrides ?? {}).filter(
                  (v) => typeof v === 'boolean',
                ).length;
                const tenantFlags = MENU_FLAGS.filter((f) =>
                  isMenuFlagVisibleForClient(f, client.name),
                );
                const visibleCount = tenantFlags.filter((f) =>
                  resolveMenuItemEnabled(f.key, features),
                ).length;

                return (
                  <Card key={client.id} className='border-gray-200 shadow-sm'>
                    <CardHeader className='border-b bg-gray-50 p-0'>
                      <button
                        type='button'
                        onClick={() => toggleExpanded(client.id)}
                        aria-expanded={isExpanded}
                        className='w-full flex items-center justify-between gap-3 p-6 text-left hover:bg-gray-100 transition-colors'
                      >
                        <div className='flex items-center gap-3'>
                          {isExpanded ? (
                            <ChevronDown className='h-4 w-4 text-brand-gray shrink-0' />
                          ) : (
                            <ChevronRight className='h-4 w-4 text-brand-gray shrink-0' />
                          )}
                          <div className='p-2 bg-white rounded-md border border-gray-200'>
                            <Building2 className='h-5 w-5 text-brand-blue' />
                          </div>
                          <div>
                            <CardTitle className='text-lg font-semibold text-brand-navy'>
                              {client.name}
                            </CardTitle>
                            {client.domain && (
                              <div className='flex items-center gap-1.5 text-xs text-brand-gray mt-0.5'>
                                <Globe className='h-3 w-3' />
                                {client.domain}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className='flex items-center gap-2'>
                          {isPending && (
                            <Loader2 className='h-4 w-4 animate-spin text-brand-blue' />
                          )}
                          <Badge
                            className={
                              features.enhancedBrokerPortal
                                ? 'bg-emerald/10 text-emerald border-emerald/20'
                                : 'bg-brand-gray/10 text-brand-gray border-brand-gray/20'
                            }
                          >
                            Master {features.enhancedBrokerPortal ? 'on' : 'off'}
                          </Badge>
                          <Badge
                            className={
                              overrideCount > 0
                                ? 'bg-amber/10 text-amber border-amber/20'
                                : 'bg-brand-gray/10 text-brand-gray border-brand-gray/20'
                            }
                          >
                            {overrideCount} override{overrideCount === 1 ? '' : 's'}
                          </Badge>
                          <Badge variant='outline'>
                            {visibleCount} / {tenantFlags.length} visible
                          </Badge>
                          <Badge
                            className={
                              resolveResourceFoldersEnabled(features)
                                ? 'bg-emerald/10 text-emerald border-emerald/20'
                                : 'bg-brand-gray/10 text-brand-gray border-brand-gray/20'
                            }
                          >
                            Folders {resolveResourceFoldersEnabled(features) ? 'on' : 'off'}
                          </Badge>
                        </div>
                      </button>
                    </CardHeader>
                    {isExpanded && (
                      <CardContent className='p-6 space-y-6'>
                        <ClientFeatureFlagControls
                          idPrefix={client.id}
                          features={features}
                          clientName={client.name}
                          disabled={!canEdit || isPending}
                          onMasterChange={(checked) =>
                            handleMasterChange(client.id, features, checked)
                          }
                          onOverrideChange={(key, next) =>
                            handleOverrideChange(client.id, features, key, next)
                          }
                          onResourceFoldersChange={(checked) =>
                            handleResourceFoldersChange(client.id, checked)
                          }
                          onRfcClientPortalAccessChange={(checked) =>
                            handleRfcClientPortalAccessChange(client.id, checked)
                          }
                          onRfcAchAuthorizationChange={(checked) =>
                            handleRfcAchAuthorizationChange(client.id, checked)
                          }
                          onSimplifiedResourceLayoutChange={(checked) =>
                            handleSimplifiedResourceLayoutChange(client.id, checked)
                          }
                          onLinkVbaGroupChange={(checked) =>
                            handleLinkVbaGroupChange(client.id, checked)
                          }
                          onBrokerDocumentSelfServiceChange={(checked) =>
                            handleBrokerDocumentSelfServiceChange(client.id, checked)
                          }
                          onVipChange={(checked) => handleVipChange(client.id, checked)}
                          onExternalTpasChange={(checked) =>
                            handleExternalTpasChange(client.id, checked)
                          }
                          onAppUserDeactivationChange={(checked) =>
                            handleAppUserDeactivationChange(client.id, checked)
                          }
                          onBulkBrokerImportChange={(checked) =>
                            handleBulkBrokerImportChange(client.id, checked)
                          }
                          onViewAsBrokerChange={(checked) =>
                            handleViewAsBrokerChange(client.id, checked)
                          }
                          onNewBrokerUiChange={(checked) =>
                            handleNewBrokerUiChange(client.id, checked)
                          }
                          onOpenEnrollmentDashboardChange={(checked) =>
                            handleOpenEnrollmentDashboardChange(client.id, checked)
                          }
                          onBrokerCommissionAmountChange={(checked) =>
                            handleBrokerCommissionAmountChange(client.id, checked)
                          }
                          onTicketingChange={(checked) =>
                            handleTicketingChange(
                              client.id,
                              client.name,
                              client.portalTicketConfig,
                              checked,
                            )
                          }
                        />
                        <AccountsViewConfigSection
                          value={features.accountsView}
                          disabled={!canEdit}
                          isPending={isPending}
                          scopeLabel='Customize the Accounts view for this tenant.'
                          onSave={(config) => handleAccountsViewSave(client.id, config)}
                          onReset={() => handleAccountsViewReset(client.id)}
                        />
                        <ClientUsersSection clientId={client.id} disabled={!canEdit} />
                      </CardContent>
                    )}
                  </Card>
                );
              })}

              {clients && clients.length === 0 && (
                <Card className='border-gray-200 shadow-sm'>
                  <CardContent className='p-8 text-center text-brand-gray'>
                    No clients found.
                  </CardContent>
                </Card>
              )}

              {clients && clients.length > 0 && filteredClients.length === 0 && (
                <Card className='border-gray-200 shadow-sm'>
                  <CardContent className='p-8 text-center text-brand-gray'>
                    No clients match “{search}”.
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </div>
      </div>

      <Dialog
        open={ticketingDialog !== null}
        onOpenChange={(open) => {
          if (!open) setTicketingDialog(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Enable ticketing for {ticketingDialog?.clientName}</DialogTitle>
            <DialogDescription>
              Sets up the per-tenant <code>PortalTicketConfig</code> alongside flipping the feature
              flag. Both fields are required on first enable; you can change them later.
            </DialogDescription>
          </DialogHeader>
          {ticketingDialog && (
            <div className='space-y-4 py-2'>
              <div className='space-y-1.5'>
                <Label htmlFor='ticketing-idPrefix'>Ticket ID prefix</Label>
                <Input
                  id='ticketing-idPrefix'
                  value={ticketingDialog.idPrefix}
                  onChange={(e) =>
                    setTicketingDialog({...ticketingDialog, idPrefix: e.target.value})
                  }
                  placeholder='SYN-'
                  autoFocus
                />
                <p className='text-xs text-brand-gray'>
                  Each ticket gets a number like{' '}
                  <code>{ticketingDialog.idPrefix || 'SYN-'}001</code>.
                </p>
              </div>
              <div className='space-y-1.5'>
                <Label htmlFor='ticketing-brandedEmailSender'>Branded email sender</Label>
                <Input
                  id='ticketing-brandedEmailSender'
                  type='email'
                  value={ticketingDialog.brandedEmailSender}
                  onChange={(e) =>
                    setTicketingDialog({
                      ...ticketingDialog,
                      brandedEmailSender: e.target.value,
                    })
                  }
                  placeholder='notifications@synrgy.com'
                />
                <p className='text-xs text-brand-gray'>
                  Goes into the From line of every ticket-event email row. Real delivery is deferred
                  for now; rows accumulate with <code>sentAt = null</code> until SendGrid is wired.
                </p>
              </div>
              <div className='space-y-1.5'>
                <Label htmlFor='ticketing-reminderHours'>Reminder interval (hours)</Label>
                <Input
                  id='ticketing-reminderHours'
                  type='number'
                  min={1}
                  max={168}
                  value={ticketingDialog.reminderHours}
                  onChange={(e) =>
                    setTicketingDialog({
                      ...ticketingDialog,
                      reminderHours: Number(e.target.value),
                    })
                  }
                />
                <p className='text-xs text-brand-gray'>
                  Defaults to 12. Drives the stuck-ticket reminder cadence; the reminder job hasn't
                  shipped yet, but this value is honored when it does.
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant='outline'
              onClick={() => setTicketingDialog(null)}
              disabled={updateFeatures.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={submitTicketingDialog}
              disabled={
                !ticketingDialog?.idPrefix.trim() ||
                !ticketingDialog?.brandedEmailSender.trim() ||
                updateFeatures.isPending
              }
            >
              {updateFeatures.isPending ? 'Enabling…' : 'Enable'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
